import React, { createContext, useContext, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';

/**
 * NotificationContext
 * 
 * Provides a centralized notification system for the app.
 * Replaces individual notification states in components.
 * 
 * Usage:
 * const { showNotification } = useNotification();
 * showNotification('Plan saved!', 'success');
 */

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const hideNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  const value = {
    showNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Global Snackbar - shows notifications from anywhere in the app */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={hideNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export default NotificationContext;

